import { useApiQuery } from '@common/hooks/query/useApiQuery';
import { SkipOrArgs } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import {
    CollectionMetadata,
    DEFAULT_COLLECTION_METADATA_RESPONSE,
    GetCollectionMetadataArgs,
    MetadataSources,
    MultilingualCollectionMetadata,
    Sources,
} from '../types';

import { useMemo } from 'react';
import { useLanguage } from '@features/language';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCollectionMetadataQuery } from '../api';
import { flattenMetadata } from '../utils/mappers';

export type UseCollectionMetadataArgs = GetCollectionMetadataArgs;

export const useCollectionMetadata = (
    args: SkipOrArgs<UseCollectionMetadataArgs>,
) => {
    const {
        currentLanguage: { code: locale },
    } = useLanguage();

    const queryArgs: SkipOrArgs<UseCollectionMetadataArgs> = useMemo(() => {
        if (args === skipToken || !args.collectionId || !args.proscopeId)
            return skipToken;
        return args;
    }, [args]);

    const { data, ...queryResult } = useApiQuery(
        useGetCollectionMetadataQuery,
        queryArgs,
    );

    const metadata = mergeWithDefault(
        data,
        DEFAULT_COLLECTION_METADATA_RESPONSE,
    );

    const flatMergedData: CollectionMetadata = useMemo(() => {
        return flattenMetadata(metadata.merged, locale);
    }, [metadata.merged, locale]);

    const flatMergedSources = useMemo(() => {
        return Object.entries(metadata.sources).reduce(
            (acc, [source, data]) => {
                acc[source as Sources] = flattenMetadata(
                    data as MultilingualCollectionMetadata,
                    locale,
                );
                return acc;
            },
            {} as MetadataSources,
        );
    }, [locale, metadata.sources]);

    return {
        data: flatMergedData,
        sources: flatMergedSources,
        ...queryResult,
    };
};
